import { Card, CardContent, CardHeader, CardTitle } from '@components/Card';
import { Button } from '@components/Button';
import type { Project } from '@services/projectService';
import { formatDistanceToNow } from 'date-fns';

interface ProjectCardProps {
  project: Project;
  onViewAnalysis: (id: string) => void;
  onStartAnalysis: (id: string) => void;
  onDelete: (id: string) => void;
}

const statusColors = {
  created: 'bg-gray-100 text-gray-700',
  analyzing: 'bg-gray-100 text-gray-700',
  analyzed: 'bg-green-50 text-green-600',
  modernizing: 'bg-purple-50 text-purple-600',
  completed: 'bg-indigo-50 text-indigo-600',
  failed: 'bg-red-50 text-red-600',
};

const sourceTypeIcons = {
  github: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
    </svg>
  ),
  gitlab: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.546 10.93L13.067.452c-.604-.603-1.582-.603-2.188 0L.452 10.93c-.603.605-.603 1.584 0 2.188l10.427 10.426c.606.605 1.584.605 2.188 0l10.48-10.426c.603-.604.603-1.582 0-2.188z"/>
    </svg>
  ),
  bitbucket: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M.778 1.213a.768.768 0 00-.768.892l3.263 19.81c.084.499.515.868 1.022.873H19.95a.772.772 0 00.77-.646l3.27-20.03a.768.768 0 00-.768-.891zM14.52 15.528H9.522L8.17 8.464h7.561z"/>
    </svg>
  ),
  local: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  ),
};

export function ProjectCard({ project, onViewAnalysis, onStartAnalysis, onDelete }: ProjectCardProps) {
  const isAnalyzing = project.analysis_status === 'analyzing';

  return (
    <Card variant="bordered" className="hover:shadow-md transition-all">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-gray-500">
                {sourceTypeIcons[project.source_type]}
              </div>
              <CardTitle className="text-lg">{project.name}</CardTitle>
            </div>
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[project.analysis_status]}`}>
              {project.analysis_status.charAt(0).toUpperCase() + project.analysis_status.slice(1)}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {project.description && (
          <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
        )}

        <div className="space-y-2 text-sm">
          <div className="flex items-center text-gray-600">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            <span className="truncate">{project.source_location}</span>
          </div>

          <div className="flex items-center text-gray-600">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>

        {/* Tech Stack Summary */}
        {(project.frontend_stack || project.backend_stack) && (
          <div className="pt-3 border-t border-gray-200">
            <div className="flex flex-wrap gap-1">
              {project.frontend_stack?.slice(0, 2).map((tech) => (
                <span
                  key={tech.name}
                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300"
                >
                  {tech.name}
                </span>
              ))}
              {project.backend_stack?.slice(0, 2).map((tech) => (
                <span
                  key={tech.name}
                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-50 text-purple-600 border border-purple-200"
                >
                  {tech.name}
                </span>
              ))}
              {((project.frontend_stack?.length || 0) + (project.backend_stack?.length || 0)) > 4 && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                  +{((project.frontend_stack?.length || 0) + (project.backend_stack?.length || 0)) - 4} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          {!isAnalyzing && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => onViewAnalysis(project.id)}
              className="flex-1"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Details
            </Button>
          )}

          {isAnalyzing && (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-600">
              <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Analyzing...
            </div>
          )}

          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(project.id)}
            className="text-red-600 hover:text-red-700 hover:border-red-300"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
